import React, { ReactNode } from 'react';

import { UnitNodeLabeled } from '@vk-hr-tek/core/units';
import { Grid } from '@vk-hr-tek/ui/Grid';
import { Button } from '@vk-hr-tek/ui/Button';
import { Icon } from '@vk-hr-tek/ui/Icon';

import { TreeViewAutocomplete } from '../../TreeViewAutocomplete';

type TreeNode<T> = {
  children?: Option<T>[];
};

type Option<T> = Omit<T, 'children'> & TreeNode<T>;

interface Filter {
  limit: number;
  offset: number;
  query: string;
}

interface UnitsInputProps {
  index: number;
  name: string;
  label: string;
  placeholder?: string;
  tooltip?: ReactNode;
  options: UnitNodeLabeled[];
  disabled?: boolean;
  onChange?: (index: number) => (values: string | undefined) => void;
  onLoadTree?: (
    unitId: string,
  ) => Promise<Option<{ id: string; label: string }>[]>;
  onChildrenLoad?: (value: string) => Promise<{ id: string; label: string }[]>;
  onSearch?: (filter: Filter) => Promise<{ id: string; label: string }[]>;
  config: {
    firstField: boolean;
    lastField: boolean;
    onAdd: () => void;
    onDelete: () => void;
  };
}

export const UnitsInput = ({
  index,
  name,
  label,
  placeholder,
  tooltip,
  options,
  disabled = false,
  onChange,
  onLoadTree,
  onChildrenLoad,
  onSearch,
  config,
}: UnitsInputProps) => {
  const { firstField, lastField, onAdd, onDelete } = config;

  return (
    <Grid container direction="row" spacing="20" wrap="nowrap">
      <Grid item xs={12} md={6}>
        <TreeViewAutocomplete
          withoutMargins
          clearable
          name={name}
          label={index === 0 ? label : undefined}
          placeholder={placeholder || label}
          tooltip={index === 0 ? tooltip : undefined}
          options={options}
          disabled={disabled}
          onChange={onChange?.(index)}
          onLoadTree={onLoadTree}
          onChildrenLoad={onChildrenLoad}
          onSearch={onSearch}
        />
      </Grid>
      <Grid
        item
        display="flex"
        flexDirection="column-reverse"
        mt={firstField ? '24' : '0'}
        pt={firstField ? '4' : '0'}
      >
        {lastField ? (
          <Button
            icon={<Icon name="add" size="24" />}
            onClick={onAdd}
            size="large"
          />
        ) : (
          <Button
            variant="tertiaryLight"
            icon={
              <Icon
                name="delete_outline"
                size="24"
                color="original.brand.primary"
              />
            }
            size="large"
            onClick={onDelete}
          />
        )}
      </Grid>
    </Grid>
  );
};
